import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, today } from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileSpreadsheet, FileText, Share2, Bell, ShieldAlert, User, Store, Phone, Send, TrendingDown, AlertTriangle, RefreshCw } from "lucide-react";
import * as XLSX from "xlsx";

function firstOfMonth() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}

export default function Reports() {
  const [frm, setFrm] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [activeTab, setActiveTab] = useState("sales");
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [collection, setCollection] = useState([]);
  const [churnAlerts, setChurnAlerts] = useState([]);

  // Auto Outstanding WhatsApp modal state
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [targetCustomer, setTargetCustomer] = useState(null);

  // Churn Alert WhatsApp Re-engagement modal state
  const [churnModalOpen, setChurnModalOpen] = useState(false);
  const [churnCustomer, setChurnCustomer] = useState(null);

  function load() {
    api.get("/reports/sales", { params: { frm, to } }).then(r => setSales(r.data)).catch(() => {});
    api.get("/reports/products", { params: { frm, to } }).then(r => setProducts(r.data)).catch(() => {});
    api.get("/reports/outstanding").then(r => setOutstanding(r.data)).catch(() => {});
    api.get("/reports/collection", { params: { frm, to } }).then(r => setCollection(r.data)).catch(() => {});
    api.get("/reports/churn-alerts").then(r => setChurnAlerts(r.data)).catch(() => {});
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function buildOutstandingReminderText(c) {
    const overdueText = c.days_overdue > 0 ? ` (Overdue by ${c.days_overdue} days)` : "";
    const lines = [
      `*Omkar Milk Distributor*`,
      `⚠️ *OUTSTANDING DUES PAYMENT REMINDER*`,
      `---------------------------------------`,
      `Dear *${c.business_name}* (${c.customer_name}),`,
      "",
      `This is a friendly automated reminder regarding your account.`,
      `*Total Pending Outstanding Dues: ₹${Number(c.outstanding).toFixed(2)}*${overdueText}`,
      `Billing Cycle: *${c.payment_cycle || 'Monthly'}*`,
    ];
    if (c.last_payment_date) {
      lines.push(`Last Payment Received: ${fmtDate(c.last_payment_date)}`);
    }
    lines.push("");
    lines.push(`Kindly arrange to clear the pending balance at your earliest convenience.`);
    lines.push(`If you have already paid, please ignore this notice.`);
    lines.push("");
    lines.push(`Thank you for your business!`);
    return lines.join("\n");
  }

  function buildChurnReengagementText(c) {
    const lines = [
      `*Omkar Milk Distributor*`,
      `🥛 *SPECIAL RE-ENGAGEMENT NOTICE & ORDER CHECK-IN*`,
      `---------------------------------------`,
      `Dear *${c.business_name}* (${c.customer_name}),`,
      "",
      `We noticed your recent dairy order volume dropped by *${c.drop_pct}%* over the last 7 days.`,
      `Recent 7 Days: *₹${c.recent_amount.toFixed(2)}* (${c.recent_orders} orders)`,
      `Previous 7 Days: *₹${c.prior_amount.toFixed(2)}* (${c.prior_orders} orders)`,
    ];
    if (c.last_order_date) {
      lines.push(`Last Order Date: *${fmtDate(c.last_order_date)}*`);
    }
    lines.push("");
    lines.push(`We want to ensure your business has fresh milk, paneer, and curd whenever needed!`);
    lines.push(`Please let us know if you need to adjust daily quantities or require special rates.`);
    lines.push("");
    lines.push(`Thank you for partnering with Omkar Milk Distributor!`);
    return lines.join("\n");
  }

  function handleNotifyCustomer(c) {
    if (c.mobile_secondary) {
      setTargetCustomer(c);
      setReminderModalOpen(true);
    } else {
      executeNotifyCustomer(c, c.mobile || "");
    }
  }

  function executeNotifyCustomer(c, phoneInput) {
    const phoneRaw = (phoneInput || "").replace(/\D/g, "");
    const phone = phoneRaw.length === 10 ? `91${phoneRaw}` : phoneRaw;
    const text = encodeURIComponent(buildOutstandingReminderText(c));
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setReminderModalOpen(false);
  }

  function handleChurnReengage(c) {
    if (c.mobile_secondary) {
      setChurnCustomer(c);
      setChurnModalOpen(true);
    } else {
      executeChurnReengage(c, c.mobile || "");
    }
  }

  function executeChurnReengage(c, phoneInput) {
    const phoneRaw = (phoneInput || "").replace(/\D/g, "");
    const phone = phoneRaw.length === 10 ? `91${phoneRaw}` : phoneRaw;
    const text = encodeURIComponent(buildChurnReengagementText(c));
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setChurnModalOpen(false);
  }

  // Helper functions for CSV & XLSX exports
  const getActiveReportData = () => {
    switch (activeTab) {
      case "sales":
        return {
          filename: `Customer_Sales_Report_${frm}_to_${to}`,
          headers: ["Customer Name", "Total Orders", "Total Sales Amount (INR)"],
          data: sales.map(s => [s.customer_name, s.orders, s.total])
        };
      case "products":
        return {
          filename: `Product_Sales_Report_${frm}_to_${to}`,
          headers: ["Product Name", "Unit", "Qty Sold", "Total Amount (INR)"],
          data: products.map(p => [p.product_name, p.unit, p.qty, p.amount])
        };
      case "outstanding":
        return {
          filename: `Outstanding_Receivables_Report_${today()}`,
          headers: ["Business Name", "Mobile Number", "Outstanding Balance (INR)", "Advance Amount (INR)"],
          data: outstanding.map(o => [o.business_name, o.mobile || "", o.outstanding, o.advance || 0])
        };
      case "collection":
        return {
          filename: `Collections_Payment_Report_${frm}_to_${to}`,
          headers: ["Payment Date", "Customer Name", "Payment Mode", "Reference Number", "Amount (INR)"],
          data: collection.map(c => [c.payment_date, c.customer_name, c.payment_mode, c.reference_no || "", c.amount])
        };
      case "churn":
        return {
          filename: `Churn_Risk_Decreasing_Orders_Report_${today()}`,
          headers: ["Business Name", "Contact Person", "Mobile", "Prior 7-Day Sales", "Recent 7-Day Sales", "Drop %", "Risk Level", "Last Order Date"],
          data: churnAlerts.map(c => [c.business_name, c.customer_name, c.mobile, c.prior_amount, c.recent_amount, `${c.drop_pct}%`, c.risk_level, c.last_order_date || "N/A"])
        };
      default:
        return null;
    }
  };

  const exportCSV = () => {
    const report = getActiveReportData();
    if (!report || !report.data.length) return;

    const csvContent = [
      report.headers.join(","),
      ...report.data.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${report.filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportXLSX = () => {
    const report = getActiveReportData();
    if (!report || !report.data.length) return;

    const worksheet = XLSX.utils.aoa_to_sheet([report.headers, ...report.data]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, `${report.filename}.xlsx`);
  };

  const getCurrentTabCount = () => {
    switch (activeTab) {
      case "sales": return sales.length;
      case "products": return products.length;
      case "outstanding": return outstanding.length;
      case "collection": return collection.length;
      case "churn": return churnAlerts.length;
      default: return 0;
    }
  };

  const currentCount = getCurrentTabCount();

  return (
    <div data-testid="reports-page">
      <PageHeader
        eyebrow="Analytics"
        title="Reports & Financial Intelligence"
        description="Comprehensive insights into sales, product volume, outstanding dues, and customer churn alerts."
      />

      <Card padding="p-4" className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-end gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold block mb-1">From Date</label>
              <Input type="date" value={frm} onChange={e=>setFrm(e.target.value)}/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold block mb-1">To Date</label>
              <Input type="date" value={to} onChange={e=>setTo(e.target.value)}/>
            </div>
            <Button onClick={load} className="bg-cobalt-500 hover:bg-cobalt-700 text-white">
              Apply Date Filter
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={exportCSV}
              disabled={currentCount === 0}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-2"
            >
              <FileText size={16} className="text-blue-600" />
              Download CSV
            </Button>
            <Button
              onClick={exportXLSX}
              disabled={currentCount === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
            >
              <FileSpreadsheet size={16} />
              Download XLSX
            </Button>
          </div>
        </div>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 flex-wrap h-auto">
          <TabsTrigger value="sales" data-testid="report-tab-sales">Customer-wise Sales</TabsTrigger>
          <TabsTrigger value="products" data-testid="report-tab-products">Product-wise Sales</TabsTrigger>
          <TabsTrigger value="outstanding" data-testid="report-tab-outstanding">Outstanding Dues</TabsTrigger>
          <TabsTrigger value="collection" data-testid="report-tab-collection">Cash Collections</TabsTrigger>
          <TabsTrigger value="churn" data-testid="report-tab-churn" className="data-[state=active]:bg-rose-500 data-[state=active]:text-white font-semibold">
            <TrendingDown size={14} className="mr-1.5" />
            Churn Alerts ({churnAlerts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sales">
          <Card padding="p-0" className="mt-4">
            {sales.length === 0 ? <div className="p-8"><Empty/></div> : (
              <table className="ddms-table">
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th className="num">Total Orders</th>
                    <th className="num">Total Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map(s => (
                    <tr key={s.customer_id}>
                      <td className="font-medium">{s.customer_name}</td>
                      <td className="num font-mono">{s.orders}</td>
                      <td className="num font-semibold text-cobalt-600">{fmtINR(s.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="products">
          <Card padding="p-0" className="mt-4">
            {products.length === 0 ? <div className="p-8"><Empty/></div> : (
              <table className="ddms-table">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Unit</th>
                    <th className="num">Quantity Sold</th>
                    <th className="num">Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p.product_id}>
                      <td className="font-medium">{p.product_name}</td>
                      <td>{p.unit}</td>
                      <td className="num font-mono">{p.qty}</td>
                      <td className="num font-semibold text-cobalt-600">{fmtINR(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="outstanding">
          <Card padding="p-0" className="mt-4">
            {outstanding.length === 0 ? <div className="p-8"><Empty/></div> : (
              <table className="ddms-table">
                <thead>
                  <tr>
                    <th>Business Name</th>
                    <th>Mobile</th>
                    <th>Billing Cycle</th>
                    <th className="num">Outstanding Balance</th>
                    <th className="num">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {outstanding.map(o => (
                    <tr key={o.id}>
                      <td className="font-medium">{o.business_name}<div className="text-[11px] text-slate-500">{o.customer_name}</div></td>
                      <td className="font-mono text-xs">{o.mobile}</td>
                      <td><span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">{o.payment_cycle}</span></td>
                      <td className="num font-mono font-bold text-rose-600">{fmtINR(o.outstanding)}</td>
                      <td className="num">
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                          onClick={() => handleNotifyCustomer(o)}
                        >
                          <Share2 size={12} className="mr-1" />
                          Send Reminder
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="collection">
          <Card padding="p-0" className="mt-4">
            {collection.length === 0 ? <div className="p-8"><Empty/></div> : (
              <table className="ddms-table">
                <thead>
                  <tr>
                    <th>Payment Date</th>
                    <th>Customer Name</th>
                    <th>Payment Mode</th>
                    <th>Reference No</th>
                    <th className="num">Amount Collected</th>
                  </tr>
                </thead>
                <tbody>
                  {collection.map(c => (
                    <tr key={c.id}>
                      <td className="font-mono text-xs">{fmtDate(c.payment_date)}</td>
                      <td className="font-medium">{c.customer_name}</td>
                      <td><span className="px-2 py-0.5 rounded bg-slate-100 text-slate-800 text-xs font-medium">{c.payment_mode}</span></td>
                      <td className="font-mono text-xs">{c.reference_no || "—"}</td>
                      <td className="num font-semibold text-emerald-600">{fmtINR(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="churn">
          <Card padding="p-4" className="mt-4 border-rose-200 bg-rose-50/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-rose-500/10 border border-rose-400/30 flex items-center justify-center">
                  <TrendingDown size={20} className="text-rose-600 animate-pulse" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
                    Churn Alert & Decreasing Order Detector
                    <span className="bg-rose-100 text-rose-700 text-xs uppercase font-bold px-2.5 py-0.5 rounded-full border border-rose-300">
                      {churnAlerts.length} At Risk
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500">Automatically flags customers whose order volume dropped by &gt;20% over the last 7 days compared to prior 7 days</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={load} className="border-slate-300 text-slate-700 hover:bg-slate-100 text-xs">
                <RefreshCw size={12} className="mr-1" /> Re-scan Signals
              </Button>
            </div>

            {churnAlerts.length === 0 ? (
              <div className="p-8 bg-white rounded-lg border border-slate-200 text-center">
                <ShieldAlert size={32} className="mx-auto mb-2 text-emerald-500" />
                <div className="font-semibold text-slate-800 text-sm">No Churn Risk Detected</div>
                <div className="text-xs text-slate-500 mt-1">All active customers are ordering steady volumes!</div>
              </div>
            ) : (
              <Card padding="p-0" className="overflow-hidden border border-slate-200 shadow-sm">
                <div className="overflow-x-auto">
                  <table className="ddms-table">
                    <thead>
                      <tr>
                        <th>Customer / Business</th>
                        <th>Mobile Contact</th>
                        <th className="num">Prior 7-Day Vol</th>
                        <th className="num">Recent 7-Day Vol</th>
                        <th className="num">Volume Drop</th>
                        <th>Risk Severity</th>
                        <th>Last Order Date</th>
                        <th className="num">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {churnAlerts.map(c => (
                        <tr key={c.customer_id} className={c.risk_level === "High Risk" ? "bg-rose-50/40" : ""}>
                          <td className="font-medium">
                            {c.business_name}
                            <div className="text-[11px] text-slate-500">{c.customer_name} ({c.payment_cycle})</div>
                          </td>
                          <td className="font-mono text-xs">{c.mobile || "—"}</td>
                          <td className="num font-mono text-slate-600">{fmtINR(c.prior_amount)}<div className="text-[10px] text-slate-400">{c.prior_orders} orders</div></td>
                          <td className="num font-mono text-slate-800 font-semibold">{fmtINR(c.recent_amount)}<div className="text-[10px] text-slate-400">{c.recent_orders} orders</div></td>
                          <td className="num font-mono font-bold text-rose-600">
                            -{c.drop_pct}%
                          </td>
                          <td>
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                              c.risk_level === "High Risk" ? "bg-rose-100 text-rose-800 border border-rose-300" : "bg-amber-100 text-amber-800 border border-amber-300"
                            }`}>
                              {c.risk_level}
                            </span>
                          </td>
                          <td className="font-mono text-xs text-slate-600">{c.last_order_date ? fmtDate(c.last_order_date) : "No orders"}</td>
                          <td className="num">
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                              onClick={() => handleChurnReengage(c)}
                            >
                              <Share2 size={12} className="mr-1" />
                              Re-engage WhatsApp
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* WhatsApp Churn Re-engagement Modal */}
      <Dialog open={churnModalOpen} onOpenChange={setChurnModalOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-2xl">
          <div className="bg-gradient-to-r from-amber-600 to-rose-600 p-5 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white text-lg font-heading font-semibold">
                <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl">
                  <TrendingDown size={20} className="text-white" />
                </div>
                Re-engage Decreasing Customer
              </DialogTitle>
            </DialogHeader>
            <div className="mt-3 flex items-center justify-between text-xs text-amber-100 bg-white/10 backdrop-blur-md rounded-lg px-3 py-2 border border-white/15">
              <div>
                <span className="opacity-75">Customer:</span> <strong className="text-white font-mono text-sm">{churnCustomer?.business_name}</strong>
              </div>
              <div className="text-right">
                <span className="opacity-75">Drop:</span> <strong className="text-amber-200 font-mono text-sm">-{churnCustomer?.drop_pct}%</strong>
              </div>
            </div>
          </div>

          <div className="p-5 bg-slate-50/50">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <Store size={14} className="text-cobalt-600" />
              <span>{churnCustomer?.business_name}</span>
              <span className="text-slate-400 font-normal">({churnCustomer?.customer_name})</span>
            </div>

            <div className="space-y-3">
              <div
                onClick={() => executeChurnReengage(churnCustomer, churnCustomer?.mobile)}
                className="group relative flex items-center justify-between p-4 bg-white hover:bg-emerald-50/60 border-2 border-emerald-500/30 hover:border-emerald-500 rounded-xl cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold transition-transform group-hover:scale-105">
                    <User size={18} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 text-sm">Owner (Primary)</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800">Primary</span>
                    </div>
                    <div className="text-xs font-mono text-slate-600 mt-0.5 flex items-center gap-1">
                      <Phone size={12} className="text-slate-400" />
                      {churnCustomer?.mobile || "N/A"}
                    </div>
                  </div>
                </div>
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-0 -translate-x-2">
                  <Send size={14} />
                </div>
              </div>

              {churnCustomer?.mobile_secondary && (
                <div
                  onClick={() => executeChurnReengage(churnCustomer, churnCustomer?.mobile_secondary)}
                  className="group relative flex items-center justify-between p-4 bg-white hover:bg-cobalt-50/60 border-2 border-slate-200 hover:border-cobalt-400 rounded-xl cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-full bg-cobalt-100 text-cobalt-700 flex items-center justify-center font-bold transition-transform group-hover:scale-105">
                      <Store size={18} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 text-sm">Cashier / Shop Manager</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700">Secondary</span>
                      </div>
                      <div className="text-xs font-mono text-slate-600 mt-0.5 flex items-center gap-1">
                        <Phone size={12} className="text-slate-400" />
                        {churnCustomer?.mobile_secondary}
                      </div>
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-cobalt-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-0 -translate-x-2">
                    <Send size={14} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-3.5 bg-slate-100 border-t border-slate-200 flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setChurnModalOpen(false)} className="rounded-lg text-xs font-medium">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Recipient Selection Modal for Outstanding Reminders */}
      <Dialog open={reminderModalOpen} onOpenChange={setReminderModalOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-2xl">
          <div className="bg-gradient-to-r from-rose-600 to-amber-600 p-5 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white text-lg font-heading font-semibold">
                <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl">
                  <Bell size={20} className="text-white" />
                </div>
                Send Outstanding Reminder
              </DialogTitle>
            </DialogHeader>
            <div className="mt-3 flex items-center justify-between text-xs text-rose-100 bg-white/10 backdrop-blur-md rounded-lg px-3 py-2 border border-white/15">
              <div>
                <span className="opacity-75">Customer:</span> <strong className="text-white font-mono text-sm">{targetCustomer?.business_name}</strong>
              </div>
              <div className="text-right">
                <span className="opacity-75">Dues:</span> <strong className="text-white font-mono text-sm">₹{Number(targetCustomer?.outstanding || 0).toFixed(2)}</strong>
              </div>
            </div>
          </div>

          <div className="p-5 bg-slate-50/50">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <Store size={14} className="text-cobalt-600" />
              <span>{targetCustomer?.business_name}</span>
              <span className="text-slate-400 font-normal">({targetCustomer?.customer_name})</span>
            </div>

            <div className="space-y-3">
              <div
                onClick={() => executeNotifyCustomer(targetCustomer, targetCustomer?.mobile)}
                className="group relative flex items-center justify-between p-4 bg-white hover:bg-emerald-50/60 border-2 border-emerald-500/30 hover:border-emerald-500 rounded-xl cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold transition-transform group-hover:scale-105">
                    <User size={18} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 text-sm">Owner (Primary)</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800">Primary</span>
                    </div>
                    <div className="text-xs font-mono text-slate-600 mt-0.5 flex items-center gap-1">
                      <Phone size={12} className="text-slate-400" />
                      {targetCustomer?.mobile || "N/A"}
                    </div>
                  </div>
                </div>
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-0 -translate-x-2">
                  <Send size={14} />
                </div>
              </div>

              {targetCustomer?.mobile_secondary && (
                <div
                  onClick={() => executeNotifyCustomer(targetCustomer, targetCustomer?.mobile_secondary)}
                  className="group relative flex items-center justify-between p-4 bg-white hover:bg-cobalt-50/60 border-2 border-slate-200 hover:border-cobalt-400 rounded-xl cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-full bg-cobalt-100 text-cobalt-700 flex items-center justify-center font-bold transition-transform group-hover:scale-105">
                      <Store size={18} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 text-sm">Cashier / Shop Manager</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700">Secondary</span>
                      </div>
                      <div className="text-xs font-mono text-slate-600 mt-0.5 flex items-center gap-1">
                        <Phone size={12} className="text-slate-400" />
                        {targetCustomer?.mobile_secondary}
                      </div>
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-cobalt-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-0 -translate-x-2">
                    <Send size={14} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-3.5 bg-slate-100 border-t border-slate-200 flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setReminderModalOpen(false)} className="rounded-lg text-xs font-medium">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
